import { unwrapAssetEnvelope } from '../api/getAssetById';
import { assetRecordPrimaryId } from './assetRecordPrimaryId';

export function isAssetRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

const WRAPPED_ARRAY_KEYS = [
  'data',
  'assets',
  'items',
  'results',
  'records',
  'facilities',
  'value',
] as const;

function canonicalAssetId(asset: Record<string, unknown>): string | null {
  return assetRecordPrimaryId(asset);
}

function imagesWireIsEmpty(wire: unknown): boolean {
  if (wire === undefined || wire === null) {
    return true;
  }
  if (Array.isArray(wire) && wire.length === 0) {
    return true;
  }
  if (typeof wire === 'string' && !wire.trim()) {
    return true;
  }
  return false;
}

const ASSET_NESTED_KEYS = ['asset', 'Asset'] as const;
const FACILITY_NESTED_KEYS = ['facility', 'Facility'] as const;

function isImageFieldKey(key: string): boolean {
  if (/logo/i.test(key)) {
    return false;
  }
  if (key === 'images' || key === 'Images') {
    return true;
  }
  return /image|img|thumbnail|thumb|photo|picture|cover|banner|avatar/i.test(key);
}

function collectNestedRecords(
  row: Record<string, unknown>,
  keys: readonly string[],
): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  for (const key of keys) {
    const nested = row[key];
    if (isAssetRecord(nested)) {
      out.push(nested);
    }
  }
  return out;
}

function fillNonImageGapsFromNested(
  merged: Record<string, unknown>,
  nested: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...merged };
  for (const [k, v] of Object.entries(nested)) {
    if (isImageFieldKey(k)) {
      continue;
    }
    const existing = out[k];
    if (
      existing === undefined ||
      existing === null ||
      (typeof existing === 'string' && !existing.trim())
    ) {
      out[k] = v;
    }
  }
  return out;
}

function liftImageScalarsFromNested(
  merged: Record<string, unknown>,
  nestedRecords: Record<string, unknown>[],
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...merged };
  for (const nested of nestedRecords) {
    for (const [k, v] of Object.entries(nested)) {
      if (typeof v !== 'string' || !v.trim()) {
        continue;
      }
      if (!isImageFieldKey(k)) {
        continue;
      }
      const existing = out[k];
      if (
        existing === undefined ||
        existing === null ||
        (typeof existing === 'string' && !existing.trim())
      ) {
        out[k] = v;
      }
    }
  }
  return out;
}

/**
 * Search/list rows sometimes put ids and images on nested `asset` / `facility` envelopes while
 * keeping display fields at the root (common on partner / legacy indexed assets).
 */
export function coalesceAssetListRecord(row: Record<string, unknown>): Record<string, unknown> {
  const assetNested = collectNestedRecords(row, ASSET_NESTED_KEYS);
  const facilityNested = collectNestedRecords(row, FACILITY_NESTED_KEYS);
  if (assetNested.length === 0 && facilityNested.length === 0) {
    return liftGeoFieldsFromNestedAddress(row);
  }

  let merged: Record<string, unknown> = { ...row };

  // Asset envelope — ids and images are asset-specific.
  for (const nested of assetNested) {
    merged = { ...nested, ...merged };
  }

  // Facility envelope — amenity/venue catalog metadata only (never shared facility images).
  for (const nested of facilityNested) {
    merged = fillNonImageGapsFromNested(merged, nested);
  }

  if (!assetRecordPrimaryId(merged)) {
    for (const nested of assetNested) {
      const id = assetRecordPrimaryId(nested);
      if (id) {
        merged.id = id;
        break;
      }
    }
  }

  const topImages = row.images ?? row.Images;
  if (imagesWireIsEmpty(topImages)) {
    for (const nested of assetNested) {
      const nestedImages = nested.images ?? nested.Images;
      if (!imagesWireIsEmpty(nestedImages)) {
        merged.images = nestedImages;
        break;
      }
    }
  }

  merged = liftImageScalarsFromNested(merged, assetNested);

  return liftGeoFieldsFromNestedAddress(merged);
}

function liftGeoFieldsFromNestedAddress(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...row };
  const liftScalar = (targetKey: string, source: Record<string, unknown>, sourceKey: string) => {
    if (out[targetKey] !== undefined && out[targetKey] !== null && out[targetKey] !== '') {
      return;
    }
    const v = source[sourceKey];
    if (v !== undefined && v !== null && v !== '') {
      out[targetKey] = v;
    }
  };

  const location = row.location;
  if (isAssetRecord(location)) {
    liftScalar('latitude', location, 'lat');
    liftScalar('latitude', location, 'latitude');
    liftScalar('longitude', location, 'lon');
    liftScalar('longitude', location, 'lng');
    liftScalar('longitude', location, 'longitude');
  }

  const addr = row.address;
  if (isAssetRecord(addr)) {
    liftScalar('country_id', addr, 'country_id');
    liftScalar('state_id', addr, 'state_id');
    liftScalar('region_id', addr, 'region_id');
    liftScalar('district_id', addr, 'district_id');
    liftScalar('country_name', addr, 'country_name');
    liftScalar('state_name', addr, 'state_name');
    liftScalar('region_name', addr, 'region_name');
    liftScalar('district_name', addr, 'district_name');
    liftScalar('city', addr, 'city');
    liftScalar('street', addr, 'street');
    liftScalar('latitude', addr, 'latitude');
    liftScalar('longitude', addr, 'longitude');
  }
  return out;
}

function normalizeAssetListRecord(row: Record<string, unknown>): Record<string, unknown> {
  return coalesceAssetListRecord(row);
}

/** Keeps the first row per `id` / `asset_id`; rows without either are all kept. */
function dedupeAssetRecordsById(records: Record<string, unknown>[]): Record<string, unknown>[] {
  const seen = new Set<string>();
  const out: Record<string, unknown>[] = [];
  for (const r of records) {
    const normalized = normalizeAssetListRecord(r);
    const key = canonicalAssetId(normalized);
    if (key === null) {
      out.push(normalized);
      continue;
    }
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(normalized);
  }
  return out;
}

/** Same fields used for the asset list title (`asset_name`, `facility_name`, `name`, then `id`). */
export function assetListSortKey(asset: Record<string, unknown>, language?: string): string {
  const label =
    (typeof asset.asset_name === 'string' && asset.asset_name.trim()) ||
    (typeof asset.facility_name === 'string' && asset.facility_name.trim()) ||
    (typeof asset.name === 'string' && asset.name.trim()) ||
    '';
  const id = assetRecordPrimaryId(asset) ?? '';
  const address =
    (typeof asset.address === 'string' && asset.address.trim()) ||
    (typeof asset.city === 'string' && asset.city.trim()) ||
    '';
  const locale = language === 'he' ? 'he' : undefined;
  return [label, id, address].join('\0').toLocaleLowerCase(locale);
}

export function sortAssetRecordsAlphabetically(
  records: Record<string, unknown>[],
  language?: string,
): Record<string, unknown>[] {
  const locale = language === 'he' ? 'he' : undefined;
  return [...records].sort((a, b) =>
    assetListSortKey(a, language).localeCompare(assetListSortKey(b, language), locale, {
      sensitivity: 'base',
      numeric: true,
    }),
  );
}

function recordsFromWrappedArrayKeyBag(o: Record<string, unknown>): Record<string, unknown>[] {
  for (const k of WRAPPED_ARRAY_KEYS) {
    const v = o[k];
    if (Array.isArray(v)) {
      return dedupeAssetRecordsById(v.filter(isAssetRecord));
    }
  }
  for (const [key, v] of Object.entries(o)) {
    const lower = key.toLowerCase();
    if (WRAPPED_ARRAY_KEYS.some((w) => w.toLowerCase() === lower) && Array.isArray(v)) {
      return dedupeAssetRecordsById(v.filter(isAssetRecord));
    }
  }
  return [];
}

function extractAssetListRecordsOnce(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return dedupeAssetRecordsById(data.filter(isAssetRecord));
  }
  if (isAssetRecord(data)) {
    const fromSearch = recordsFromSearchHits(data);
    if (fromSearch.length > 0) {
      return fromSearch;
    }
    return recordsFromWrappedArrayKeyBag(data);
  }
  return [];
}

/** Elasticsearch `POST /search/assets` — `{ hits: { hits: [{ _id, _source: asset }] } }`. */
function recordFromSearchHit(hit: Record<string, unknown>): Record<string, unknown> | null {
  const src = hit._source;
  if (!isAssetRecord(src)) {
    return null;
  }
  let row = normalizeAssetListRecord({ ...src });
  if (!assetRecordPrimaryId(row)) {
    const hitId = hit._id ?? hit.id;
    const picked =
      typeof hitId === 'string' && hitId.trim()
        ? hitId.trim()
        : typeof hitId === 'number' && Number.isFinite(hitId)
          ? String(hitId)
          : null;
    if (picked) {
      row = { ...row, id: picked };
    }
  }
  return row;
}

function recordsFromSearchHits(root: Record<string, unknown>): Record<string, unknown>[] {
  const hitsWrap = root.hits;
  if (!isAssetRecord(hitsWrap)) {
    return [];
  }
  const hits = hitsWrap.hits;
  if (!Array.isArray(hits)) {
    return [];
  }
  const records: Record<string, unknown>[] = [];
  for (const hit of hits) {
    if (!isAssetRecord(hit)) {
      continue;
    }
    const row = recordFromSearchHit(hit);
    if (row) {
      records.push(row);
    }
  }
  return dedupeAssetRecordsById(records);
}

/** Same peeling as GET-by-id, then nested `data.assets`-style wrappers. */
function listPayloadCandidates(raw: unknown): unknown[] {
  const out: unknown[] = [];
  const push = (v: unknown) => {
    if (v !== undefined && v !== null && !out.includes(v)) {
      out.push(v);
    }
  };
  push(raw);
  push(unwrapAssetEnvelope(raw));
  if (isAssetRecord(raw)) {
    const dataVal = raw.data ?? raw.Data;
    push(dataVal);
    if (isAssetRecord(dataVal)) {
      push(unwrapAssetEnvelope(dataVal));
    }
  }
  const unwrapped = unwrapAssetEnvelope(raw);
  if (isAssetRecord(unwrapped)) {
    const dataVal = unwrapped.data ?? unwrapped.Data;
    push(dataVal);
  }
  return out;
}

/**
 * Turns GET /assets-style payloads into a flat list of asset objects.
 * At most one entry per asset id (`id` or `asset_id`).
 * Sorted A–Z by display name (`asset_name` / `facility_name` / `name` / `id`).
 */
export function normalizeAssetListResponse(data: unknown): Record<string, unknown>[] {
  let best: Record<string, unknown>[] = [];
  for (const candidate of listPayloadCandidates(data)) {
    const list = extractAssetListRecordsOnce(candidate);
    if (list.length > best.length) {
      best = list;
    }
    if (list.length > 0 && best.length === list.length) {
      break;
    }
  }
  return sortAssetRecordsAlphabetically(best);
}

/** True when the API clearly returned an empty list (array or known wrapper key). */
export function isExplicitlyEmptyAssetList(data: unknown): boolean {
  const list = normalizeAssetListResponse(data);
  if (list.length > 0) {
    return false;
  }
  for (const candidate of listPayloadCandidates(data)) {
    if (Array.isArray(candidate) && candidate.length === 0) {
      return true;
    }
    if (isAssetRecord(candidate)) {
      for (const k of WRAPPED_ARRAY_KEYS) {
        if (k in candidate && Array.isArray(candidate[k]) && (candidate[k] as unknown[]).length === 0) {
          return true;
        }
      }
    }
  }
  return false;
}
